import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setFilter, resetFilters, addCategory, editCategory, removeCategory } from '../../redux/slices/categorySlice';
import { FiSearch, FiPlus, FiEdit, FiTrash2, FiTag } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useSelector as useProductSelector } from 'react-redux';
import { removeProduct, addProduct, editProduct } from '../../redux/slices/productSlice';
import { useTranslation } from 'react-i18next';
import Dropdown from '../../components/Dropdown';

const Categories = () => {
  const dispatch = useDispatch();
  const categories = useSelector(state => state.categories.filtered);
  const products = useProductSelector(state => state.products.products);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [deleteId, setDeleteId] = useState(null);
  const [bulkAction, setBulkAction] = useState('');
  const [moveToCategory, setMoveToCategory] = useState('');
  const [bulkCategory, setBulkCategory] = useState('');
  const lastBulkAction = useRef({ type: '', products: [] });
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const { t } = useTranslation();

  // Handlers
  const openAddModal = () => {
    setForm({ name: '', description: '' });
    setEditMode(false);
    setEditId(null);
    setShowModal(true);
  };
  const openEditModal = (category) => {
    setForm({ ...category });
    setEditMode(true);
    setEditId(category.id);
    setShowModal(true);
  };
  const handleFormChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = e => {
    e.preventDefault();
    if (!form.name) return;
    if (editMode) {
      dispatch(editCategory({ ...form, id: editId }));
      toast.success('Category updated!');
    } else {
      dispatch(addCategory({ ...form }));
      toast.success('Category added!');
    }
    setShowModal(false);
  };
  const handleDelete = id => setDeleteId(id);
  const confirmDelete = () => {
    dispatch(removeCategory(deleteId));
    setDeleteId(null);
    toast.success('Category deleted!');
  };
  const handleSearch = e => {
    setSearch(e.target.value);
    dispatch(setFilter({ search: e.target.value }));
  };
  const handleReset = () => {
    setSearch('');
    dispatch(resetFilters());
  };

  return (
    <div className="p-2 md:p-8 font-sans bg-gradient-to-br from-blue-50 via-white to-blue-100 min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <h2 className="text-2xl font-extrabold flex items-center gap-2 text-blue-700"><FiTag /> {t('categoriesManagement')}</h2>
        <button
          onClick={openAddModal}
          className="bg-blue-600 text-white px-6 py-2 rounded-full shadow-lg hover:bg-blue-700 transition flex items-center gap-2 text-lg"
        >
          <FiPlus /> {t('addCategory')}
        </button>
      </div>
      <div className="bg-white/90 rounded-xl shadow-xl flex flex-col md:flex-row gap-2 md:gap-4 p-2 md:p-4 mb-8 items-center w-full">
        <div className="flex items-center w-full md:w-auto gap-2 flex-1">
          <FiSearch className="text-gray-400" />
          <input
            type="text"
            placeholder={t('searchCategories')}
            value={search}
            onChange={handleSearch}
            className="border rounded-full px-4 py-2 w-full md:w-64 focus:ring-2 focus:ring-blue-200"
          />
          {search && <button onClick={handleReset} className="ml-2 text-sm text-gray-500">{t('reset')}</button>}
        </div>
      </div>
      <div className="flex items-center gap-2 mb-4">
        <Dropdown
          value={bulkCategory}
          onChange={setBulkCategory}
          options={[
            { value: '', label: t('selectCategory') },
            ...categories.map(c => ({ value: c.name, label: c.name })),
          ]}
          placeholder={t('selectCategory')}
          className="min-w-[150px]"
        />
        <Dropdown
          value={bulkAction}
          onChange={setBulkAction}
          options={[
            { value: '', label: t('bulkActions') },
            { value: 'delete', label: t('deleteAllProducts') },
            { value: 'move', label: t('moveAllProductsTo') },
          ]}
          placeholder={t('bulkActions')}
          className="min-w-[150px]"
        />
        {bulkAction === 'move' && (
          <Dropdown
            value={moveToCategory}
            onChange={setMoveToCategory}
            options={[
              { value: '', label: t('selectTargetCategory') },
              ...categories.filter(c => c.name !== bulkCategory).map(c => ({ value: c.name, label: c.name })),
            ]}
            placeholder={t('selectTargetCategory')}
            className="min-w-[150px]"
          />
        )}
        <button
          className="bg-blue-600 text-white px-6 py-2 rounded-full font-semibold shadow hover:bg-blue-700 transition disabled:opacity-50"
          disabled={!bulkCategory || !bulkAction || (bulkAction === 'move' && !moveToCategory)}
          onClick={() => setShowBulkConfirm(true)}
        >{t('apply')}</button>
      </div>
      {showBulkConfirm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 rounded-2xl shadow-xl p-6 md:p-8 w-full max-w-sm mx-2 relative animate-fade-in">
            <h3 className="text-2xl font-bold mb-6 text-center">{t('confirmBulkAction')}</h3>
            <p className="mb-6 text-center">{t('areYouSureBulkAction', { action: bulkAction === 'delete' ? t('delete') : t('move'), to: moveToCategory, category: bulkCategory })}</p>
            <div className="flex gap-4 justify-center">
              <button type="button" onClick={() => setShowBulkConfirm(false)} className="bg-gray-200 px-6 py-2 rounded-full font-semibold shadow hover:bg-gray-300 transition">{t('cancel')}</button>
              <button type="button" onClick={() => {
                // Store affected products for undo
                const affected = products.filter(p => p.category === bulkCategory);
                lastBulkAction.current = {
                  type: bulkAction,
                  products: affected.map(p => ({ ...p })),
                  from: bulkCategory,
                  to: moveToCategory,
                };
                if (bulkAction === 'delete') {
                  affected.forEach(p => dispatch(removeProduct(p.id)));
                  toast(
                    <span>{t('bulkDeleteApplied')} <button className="text-blue-600 underline ml-2" onClick={() => {
                      lastBulkAction.current.products.forEach(p => dispatch(addProduct(p)));
                      toast.success(t('undoProductsRestored'));
                    }}>{t('undo')}</button></span>,
                    { autoClose: 5000 }
                  );
                } else if (bulkAction === 'move') {
                  affected.forEach(p => dispatch(editProduct({ ...p, category: moveToCategory })));
                  toast(
                    <span>{t('bulkMoveApplied')} <button className="text-blue-600 underline ml-2" onClick={() => {
                      lastBulkAction.current.products.forEach(p => dispatch(editProduct({ ...p, category: lastBulkAction.current.from })));
                      toast.success(t('undoProductsMovedBack'));
                    }}>{t('undo')}</button></span>,
                    { autoClose: 5000 }
                  );
                }
                setBulkAction('');
                setMoveToCategory('');
                setBulkCategory('');
                setShowBulkConfirm(false);
              }} className="bg-blue-600 text-white px-6 py-2 rounded-full font-semibold shadow hover:bg-blue-700 transition">{t('confirm')}</button>
            </div>
          </div>
        </div>
      )}
      <div className="overflow-x-auto w-full rounded-xl shadow-xl bg-white/90 ring-1 ring-blue-100">
        <table className="min-w-full text-sm md:text-base">
          <thead className="bg-blue-50">
            <tr>
              <th className="px-4 py-2 text-left">{t('name')}</th>
              <th className="px-4 py-2 text-left">{t('description')}</th>
              <th className="px-4 py-2 text-center">{t('numProducts')}</th>
              <th className="px-4 py-2 text-center">{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {categories.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center py-8 text-gray-400">{t('noCategoriesFound')}</td>
              </tr>
            )}
            {categories.map(category => (
              <tr key={category.id} className="border-b last:border-b-0 hover:bg-blue-50 transition">
                <td className="px-4 py-2 font-semibold">{category.name}</td>
                <td className="px-4 py-2">{category.description}</td>
                <td className="px-4 py-2 text-center">{products.filter(p => p.category === category.name).length}</td>
                <td className="px-4 py-2 text-center flex gap-2 justify-center">
                  <button onClick={() => window.open(`/admin/products/category/${encodeURIComponent(category.name)}`, '_blank')} className="bg-green-600 text-white px-3 py-1 rounded-full font-semibold text-xs shadow hover:bg-green-700 transition">{t('manageProducts')}</button>
                  <button onClick={() => openEditModal(category)} className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full font-semibold text-xs shadow hover:bg-yellow-200 transition"><FiEdit /></button>
                  <button
                    onClick={() => handleDelete(category.id)}
                    className={`bg-red-600 text-white px-3 py-1 rounded-full font-semibold text-xs shadow hover:bg-red-700 transition ${products.filter(p => p.category === category.name).length > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={products.filter(p => p.category === category.name).length > 0}
                    title={products.filter(p => p.category === category.name).length > 0 ? t('cannotDeleteCategoryWithProducts') : ''}
                  >
                    <FiTrash2 />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 rounded-2xl shadow-xl p-6 md:p-8 w-full max-w-md mx-2 relative animate-fade-in">
            <h3 className="text-2xl font-bold mb-6 text-center">{editMode ? t('editCategory') : t('addCategory')}</h3>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <input
                type="text"
                name="name"
                placeholder={t('categoryName')}
                value={form.name}
                onChange={handleFormChange}
                className="border rounded-full px-4 py-3 w-full focus:ring-2 focus:ring-blue-200"
                required
              />
              <textarea
                name="description"
                placeholder={t('description')}
                value={form.description}
                onChange={handleFormChange}
                className="border rounded-xl px-4 py-3 w-full focus:ring-2 focus:ring-blue-200"
                rows={3}
              />
              <div className="flex gap-4 justify-center mt-6">
                <button type="button" onClick={() => setShowModal(false)} className="bg-gray-200 px-6 py-2 rounded-full font-semibold shadow hover:bg-gray-300 transition">{t('cancel')}</button>
                <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded-full font-semibold shadow hover:bg-blue-700 transition">{editMode ? t('update') : t('add')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 rounded-2xl shadow-xl p-6 md:p-8 w-full max-w-sm mx-2 relative animate-fade-in">
            <h3 className="text-2xl font-bold mb-6 text-center">{t('deleteCategory')}</h3>
            <p className="mb-6 text-center">{t('areYouSureDeleteCategory')}</p>
            <div className="flex gap-4 justify-center">
              <button onClick={() => setDeleteId(null)} className="bg-gray-200 px-6 py-2 rounded-full font-semibold shadow hover:bg-gray-300 transition">{t('cancel')}</button>
              <button onClick={confirmDelete} className="bg-red-600 text-white px-6 py-2 rounded-full font-semibold shadow hover:bg-red-700 transition">{t('delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;
export { Categories }; 